import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import PlayerCard from "../components/PlayerCard";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { CreditCard, Bookmark, Eye, UserCircle, ArrowRight, Loader2, UserCog } from "lucide-react";

const Dashboard = () => {
  const { t, user, authLoading, watchlist, refreshUser, hasActiveSub, isBusiness } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) {
      setConfirming(false);
      await refreshUser();
      return;
    }
    try {
      const { data } = await api.get(`/subscriptions/status/${sessionId}`);
      if (data.status && ["trialing", "active"].includes(data.status)) {
        setConfirming(false);
        await refreshUser();
        toast({ description: t.sub.started });
        return;
      }
    } catch {}
    setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
  }, [refreshUser, t, toast]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      setConfirming(true);
      toast({ description: t.sub.checking });
      pollStatus(sessionId);
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasActiveSub || (user && user.role === "player")) {
      api.get("/players").then(({ data }) => setPlayers(data || [])).catch(() => {});
    }
  }, [hasActiveSub, user]);

  if (authLoading) {
    return (
      <div className="pt-32 min-h-screen text-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }
  if (!user) return null;

  const watchedPlayers = players.filter((p) => watchlist.includes(p.id));
  const sub = user.subscription;
  const roleLabel = t.roles[user.role] || user.role || "—";

  const statusLabel = sub ? (t.sub[sub.status] || sub.status) : t.dashboard.noSub;
  const subDetail = sub
    ? (sub.status === "trialing" && sub.trial_end
        ? `${t.sub.trialEnds}: ${new Date(sub.trial_end).toLocaleDateString()}`
        : sub.current_period_end ? `${t.sub.renews}: ${new Date(sub.current_period_end).toLocaleDateString()}` : "")
    : "";

  const summary = [
    { icon: CreditCard, label: t.dashboard.subscription, value: sub ? `${sub.name} · ${statusLabel}` : t.dashboard.noSub, accent: hasActiveSub ? "text-emerald-400" : "text-gray-400" },
    { icon: Bookmark, label: t.dashboard.watchlist, value: watchlist.length, accent: "text-blue-400" },
    { icon: UserCircle, label: t.dashboard.role, value: roleLabel, accent: "text-orange-400" },
  ];

  return (
    <div className="pt-16 min-h-screen">
      <div className="border-b border-white/5 bg-[#0a0c10]">
        <div className="max-w-6xl mx-auto px-5 py-12 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-blue-400 text-sm font-medium">{t.dashboard.welcome}</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-1">{user.name}</h1>
          </div>
          {user.role === "player" && (
            <Button onClick={() => navigate("/my-profile")} className="bg-blue-600 hover:bg-blue-500 text-white h-10 rounded-xl">
              <UserCog className="w-4 h-4 mr-2" /> {t.myprofile.title}
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-10">
        {confirming && (
          <div className="mb-6 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-center gap-3 text-blue-300">
            <Loader2 className="w-4 h-4 animate-spin" /> {t.sub.checking}
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-5 mb-10">
          {summary.map((s, i) => (
            <div key={i} className="bg-[#0f1116] border border-white/8 rounded-2xl p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <s.icon className={`w-5 h-5 ${s.accent}`} />
                </div>
                <div>
                  <div className="text-xs text-gray-500">{s.label}</div>
                  <div className={`font-semibold ${s.accent}`}>{s.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Subscription banner for business roles */}
        {isBusiness && (
          <div className="bg-gradient-to-br from-blue-600/15 to-[#0f1116] border border-blue-500/20 rounded-2xl p-7 mb-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-bold text-white">{t.dashboard.subscription}</h2>
              <p className="text-gray-400 text-sm mt-1">{sub ? `${sub.name} · ${statusLabel}${subDetail ? " · " + subDetail : ""}` : t.sub.needSub}</p>
            </div>
            <Button onClick={() => navigate(hasActiveSub ? "/search" : "/pricing")} className="bg-blue-600 hover:bg-blue-500 text-white h-10 rounded-xl">
              {hasActiveSub ? t.dashboard.browse : t.sub.viewPlans} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Watchlist */}
        {(hasActiveSub || user.role === "player") && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold text-white">{t.dashboard.watchlist}</h2>
              <Button onClick={() => navigate("/search")} variant="outline" className="border-white/10 bg-white/5 hover:bg-white/10 text-gray-200 h-9">
                {t.dashboard.browse}
              </Button>
            </div>
            {watchedPlayers.length === 0 ? (
              <div className="bg-[#0f1116] border border-white/8 border-dashed rounded-2xl py-16 text-center">
                <Eye className="w-8 h-8 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500">{t.dashboard.watchlistEmpty}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {watchedPlayers.map((p) => <PlayerCard key={p.id} player={p} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
